import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Helmet } from 'react-helmet';
import jsPDF from 'jspdf';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

import { User, CreditCard } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { MembershipNomineesSection } from "@/components/MembershipNomineesSection";
import { MembershipDocumentsSection } from "@/components/MembershipDocumentsSection";
import { MembershipTermsSection } from "@/components/MembershipTermsSection";

const membershipSchema = z.object({
  // Personal Information
  fullNames: z.string().min(2, "Full names are required"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  idNumber: z.string().min(6, "Valid ID number is required"),
  department: z.string().min(2, "Department is required"),
  dutyStation: z.string().min(2, "Duty station is required"),
  staffNumber: z.string().min(2, "Staff number is required"),
  postalAddress: z.string().optional(),
  postCode: z.string().optional(),
  town: z.string().optional(),
  officeTelephone: z.string().optional(),
  mobilePhone: z.string().min(10, "Valid mobile phone number is required"),
  emailAddress: z.string().email("Valid email address is required"),
  
  // Witness Information
  witnessName: z.string().min(2, "Witness name is required"),
  witnessIdNumber: z.string().min(6, "Witness ID number is required"),
  witnessDutyStation: z.string().optional(),
  
  // Payment Commitment
  monthlyContribution: z.number().min(2000, "Minimum contribution is Ksh. 2,000"),
  paymentMethod: z.enum(["Payslip Checkoff", "Direct Deposit"]),
  startMonth: z.string().min(1, "Start month is required"),
  registeredMpesa: z.string().min(10, "M-Pesa number is required"),
  
  // Nominee 1 (Required)
  nominee1FullNames: z.string().min(2, "Nominee 1 full names are required"),
  nominee1DateOfBirth: z.string().optional(),
  nominee1IdNumber: z.string().min(6, "Nominee 1 ID number is required"),
  nominee1Relationship: z.string().optional(),
  nominee1PostalAddress: z.string().optional(),
  nominee1Email: z.string().email().optional().or(z.literal("")),
  nominee1Phone: z.string().optional(),
  nominee1Percentage: z.number().min(1).max(100, "Percentage must be between 1-100"),
  
  // Nominee 2 (Optional)
  nominee2FullNames: z.string().optional(),
  nominee2DateOfBirth: z.string().optional(),
  nominee2IdNumber: z.string().optional(),
  nominee2Relationship: z.string().optional(),
  nominee2PostalAddress: z.string().optional(),
  nominee2Email: z.string().email().optional().or(z.literal("")),
  nominee2Phone: z.string().optional(),
  nominee2Percentage: z.number().min(0).max(100).optional(),
  
  // Terms Agreement
  termsAgreed: z.boolean().refine(val => val === true, "You must agree to the terms and conditions")
}).refine(data => {
  if (data.nominee2FullNames) {
    const total = data.nominee1Percentage + (data.nominee2Percentage || 0);
    return total === 100;
  }
  return data.nominee1Percentage === 100;
}, {
  message: "Nominee percentages must total 100%",
  path: ["nominee1Percentage"]
});

type MembershipFormData = z.infer<typeof membershipSchema>;

export const MembershipApplication = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{
    kraPinDocument?: File;
    idCopyDocument?: File;
    passportPhoto?: File;
  }>({});

  const form = useForm<MembershipFormData>({
    resolver: zodResolver(membershipSchema),
    defaultValues: {
      monthlyContribution: 2000,
      nominee1Percentage: 100,
      nominee2Percentage: 0,
      termsAgreed: false
    }
  });

  const handleFileUpload = (field: 'kraPinDocument' | 'idCopyDocument' | 'passportPhoto', file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    setUploadedFiles(prev => ({ ...prev, [field]: file }));
    toast.success(`${file.name} uploaded successfully`);
  };

  // Helper function to read file as base64
  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const generatePDF = async (data: MembershipFormData) => {
    const pdf = new jsPDF();
    
    // Set all text to black by default
    pdf.setTextColor(0, 0, 0);
    
    // === HEADER SECTION ===
    // Logo placeholder at top center (approximately x=85, y=10)
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('NATCON STAFF SACCO', 105, 15, { align: 'center' });
    
    // Contact details on the left
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.text('P.O. BOX 21046 - 00100, NAIROBI', 20, 25);
    pdf.text('TEL: (020) 712098, 0700021222', 20, 30);
    pdf.setFontSize(9);
    pdf.text('Email: natconsacco@nca.go.ke', 20, 35);
    
    // === AFFIX PHOTO HERE BOX (TOP RIGHT - CRITICAL) ===
    const photoBoxX = 145;
    const photoBoxY = 20;
    const photoBoxWidth = 50;
    const photoBoxHeight = 60;
    
    // Draw the "AFFIX PHOTO HERE" box
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.rect(photoBoxX, photoBoxY, photoBoxWidth, photoBoxHeight);
    
    // Add passport photo if uploaded
    if (uploadedFiles.passportPhoto) {
      try {
        const imgData = await readFileAsBase64(uploadedFiles.passportPhoto);
        pdf.addImage(imgData, 'JPEG', photoBoxX + 2, photoBoxY + 2, photoBoxWidth - 4, photoBoxHeight - 4);
      } catch (error) {
        console.error('Error adding passport photo:', error);
        // If photo fails, show text in the box
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('AFFIX', photoBoxX + 25, photoBoxY + 25, { align: 'center' });
        pdf.text('PHOTO', photoBoxX + 25, photoBoxY + 35, { align: 'center' });
        pdf.text('HERE', photoBoxX + 25, photoBoxY + 45, { align: 'center' });
      }
    } else {
      // No photo uploaded - show placeholder text
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('AFFIX', photoBoxX + 25, photoBoxY + 25, { align: 'center' });
      pdf.text('PHOTO', photoBoxX + 25, photoBoxY + 35, { align: 'center' });
      pdf.text('HERE', photoBoxX + 25, photoBoxY + 45, { align: 'center' });
    }
    
    // === TITLE ===
    let yPos = 50;
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text('MEMBERSHIP APPLICATION FORM', 20, yPos);
    
    // === PERSONAL DETAILS SECTION ===
    yPos += 10;
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Personal Details', 20, yPos);
    yPos += 7;
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    
    // FULL NAMES with underline
    pdf.text('FULL NAMES:', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(`MR/MRS/MS/${data.fullNames}`, 50, yPos);
    pdf.line(50, yPos + 1, 190, yPos + 1); // Underline
    yPos += 7;
    
    // DATE OF BIRTH, ID NO, STAFF NO on one line
    pdf.setFont(undefined, 'bold');
    pdf.text('DATE OF BIRTH', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.dateOfBirth, 50, yPos);
    pdf.line(50, yPos + 1, 85, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('ID NO', 95, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.idNumber, 110, yPos);
    pdf.line(110, yPos + 1, 140, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('STAFF NO', 150, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.staffNumber, 170, yPos);
    pdf.line(170, yPos + 1, 190, yPos + 1);
    yPos += 7;
    
    // DEPARTMENT and DUTY STATION on one line
    pdf.setFont(undefined, 'bold');
    pdf.text('DEPARTMENT', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.department, 50, yPos);
    pdf.line(50, yPos + 1, 110, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('DUTY STATION', 120, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.dutyStation, 150, yPos);
    pdf.line(150, yPos + 1, 190, yPos + 1);
    yPos += 7;
    
    // POSTAL ADDRESS, POST CODE, TOWN on one line
    pdf.setFont(undefined, 'bold');
    pdf.text('POSTAL ADDRESS', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.postalAddress || '', 55, yPos);
    pdf.line(55, yPos + 1, 110, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('POST CODE', 120, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.postCode || '', 145, yPos);
    pdf.line(145, yPos + 1, 165, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('TOWN', 172, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.town || '', 185, yPos);
    pdf.line(185, yPos + 1, 190, yPos + 1);
    yPos += 7;
    
    // OFFICE TEL NUMBER and MOBILE PHONE NO on one line
    pdf.setFont(undefined, 'bold');
    pdf.text('OFFICE TEL NUMBER', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.officeTelephone || '', 60, yPos);
    pdf.line(60, yPos + 1, 110, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('MOBILE PHONE NO', 120, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.mobilePhone, 160, yPos);
    pdf.line(160, yPos + 1, 190, yPos + 1);
    yPos += 7;
    
    // E-mail Address
    pdf.setFont(undefined, 'bold');
    pdf.text('E-mail Address:', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.emailAddress, 50, yPos);
    pdf.line(50, yPos + 1, 190, yPos + 1);
    yPos += 10;
    
    // Note in smaller font
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'italic');
    pdf.text('(Applicant to attach clear copies of ID and/or Passport, Copy of PIN Certificate and a passport photo with your name and signature)', 20, yPos);
    yPos += 7;
    
    // Horizontal line separator
    pdf.setDrawColor(0, 0, 0);
    pdf.line(20, yPos, 190, yPos);
    yPos += 7;
    
    // === WITNESS SECTION ===
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'bold');
    pdf.text('Witness (Sacco Member)', 20, yPos);
    yPos += 7;
    
    pdf.setFontSize(10);
    // NAME and SIGNATURE on one line
    pdf.text('NAME', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.witnessName, 35, yPos);
    pdf.line(35, yPos + 1, 110, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('SIGNATURE', 120, yPos);
    pdf.line(145, yPos + 1, 190, yPos + 1);
    yPos += 7;
    
    // DUTY STATION, ID NO, DATE on one line
    pdf.text('DUTY STATION', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.witnessDutyStation || '', 50, yPos);
    pdf.line(50, yPos + 1, 110, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('ID NO', 120, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.witnessIdNumber, 135, yPos);
    pdf.line(135, yPos + 1, 165, yPos + 1);
    
    pdf.setFont(undefined, 'bold');
    pdf.text('DATE', 172, yPos);
    pdf.line(182, yPos + 1, 190, yPos + 1);
    yPos += 7;
    
    // Horizontal line separator
    pdf.line(20, yPos, 190, yPos);
    yPos += 7;
    
    // === OTHER DETAILS SECTION ===
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'bold');
    pdf.text('Other Details', 20, yPos);
    yPos += 7;
    
    pdf.setFontSize(10);
    pdf.text('REGISTERED M-PESA NUMBER', 20, yPos);
    pdf.setFont(undefined, 'normal');
    pdf.text(data.registeredMpesa, 80, yPos);
    pdf.line(80, yPos + 1, 190, yPos + 1);
    yPos += 10;
    
    // Payment commitment paragraph
    pdf.setFont(undefined, 'normal');
    const commitmentText = `I hereby commit to pay NATCON Sacco Ltd Kshs ${data.monthlyContribution.toLocaleString()} monthly through ${data.paymentMethod} with effect from the month of ${data.startMonth} until further notice.`;
    const splitCommitment = pdf.splitTextToSize(commitmentText, 170);
    pdf.text(splitCommitment, 20, yPos);
    yPos += splitCommitment.length * 5 + 5;
    
    // Declaration paragraph
    const declarationText = 'I hereby make an application for membership and agree to conform to NATcon Sacco by-laws and any amendment thereof and authorize that the below will be used as specimen signatures for any transactions with NATCON Sacco.';
    const splitDeclaration = pdf.splitTextToSize(declarationText, 170);
    pdf.text(splitDeclaration, 20, yPos);
    yPos += splitDeclaration.length * 5 + 10;
    
    // === SIGNATURE BOX ===
    pdf.setFont(undefined, 'bold');
    pdf.text('Signature of Applicant (Within the box)', 20, yPos);
    
    // Draw signature box
    const sigBoxX = 105;
    const sigBoxY = yPos + 5;
    const sigBoxWidth = 85;
    const sigBoxHeight = 30;
    pdf.rect(sigBoxX, sigBoxY, sigBoxWidth, sigBoxHeight);
    
    yPos += 40;
    
    // === PAGE FOOTER ===
    pdf.setFontSize(9);
    pdf.setFont(undefined, 'normal');
    pdf.text('Page | 1', 20, 285);
    
    // === PAGE 2: NOMINEE BENEFICIARIES (if needed) ===
    if (data.nominee1FullNames || data.nominee2FullNames) {
      pdf.addPage();
      yPos = 20;
      
      // Header for page 2
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('NATCON STAFF SACCO', 105, 15, { align: 'center' });
      
      pdf.setFontSize(12);
      yPos = 30;
      pdf.text('Beneficiaries Nomination Form', 20, yPos);
      yPos += 10;
      
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');
      pdf.text('Nominated Beneficiaries (if nominating more than two nominees print & attach separate form)', 20, yPos);
      yPos += 10;
      
      // Nominee 1
      if (data.nominee1FullNames) {
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'bold');
        pdf.text('FULL NAMES:', 20, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee1FullNames + ' (As they appear on ID)', 55, yPos);
        pdf.line(55, yPos + 1, 190, yPos + 1);
        yPos += 7;
        
        pdf.setFont(undefined, 'bold');
        pdf.text('DATE OF BIRTH', 20, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee1DateOfBirth || '', 50, yPos);
        pdf.line(50, yPos + 1, 85, yPos + 1);
        
        pdf.setFont(undefined, 'bold');
        pdf.text('ID NO', 95, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee1IdNumber, 110, yPos);
        pdf.line(110, yPos + 1, 140, yPos + 1);
        
        pdf.setFont(undefined, 'bold');
        pdf.text('RELATIONSHIP', 150, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee1Relationship || '', 180, yPos);
        pdf.line(180, yPos + 1, 190, yPos + 1);
        yPos += 7;
        
        pdf.setFont(undefined, 'bold');
        pdf.text('POSTAL ADDRESS', 20, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee1PostalAddress || '', 60, yPos);
        pdf.line(60, yPos + 1, 120, yPos + 1);
        
        pdf.setFont(undefined, 'bold');
        pdf.text('PERCENTAGE ASSIGNED', 130, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(`${data.nominee1Percentage}%`, 175, yPos);
        pdf.line(175, yPos + 1, 190, yPos + 1);
        yPos += 7;
        
        pdf.setFont(undefined, 'bold');
        pdf.text('E-MAIL ADDRESS', 20, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee1Email || '', 55, yPos);
        pdf.line(55, yPos + 1, 120, yPos + 1);
        
        pdf.setFont(undefined, 'bold');
        pdf.text('TELEPHONE', 130, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee1Phone || '', 160, yPos);
        pdf.line(160, yPos + 1, 190, yPos + 1);
        yPos += 15;
      }
      
      // Nominee 2 (if provided)
      if (data.nominee2FullNames) {
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'bold');
        pdf.text('FULL NAMES:', 20, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee2FullNames + ' (As they appear on ID)', 55, yPos);
        pdf.line(55, yPos + 1, 190, yPos + 1);
        yPos += 7;
        
        pdf.setFont(undefined, 'bold');
        pdf.text('DATE OF BIRTH', 20, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee2DateOfBirth || '', 50, yPos);
        pdf.line(50, yPos + 1, 85, yPos + 1);
        
        pdf.setFont(undefined, 'bold');
        pdf.text('ID NO', 95, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee2IdNumber || '', 110, yPos);
        pdf.line(110, yPos + 1, 140, yPos + 1);
        
        pdf.setFont(undefined, 'bold');
        pdf.text('RELATIONSHIP', 150, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee2Relationship || '', 180, yPos);
        pdf.line(180, yPos + 1, 190, yPos + 1);
        yPos += 7;
        
        pdf.setFont(undefined, 'bold');
        pdf.text('POSTAL ADDRESS', 20, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee2PostalAddress || '', 60, yPos);
        pdf.line(60, yPos + 1, 120, yPos + 1);
        
        pdf.setFont(undefined, 'bold');
        pdf.text('PERCENTAGE ASSIGNED', 130, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(`${data.nominee2Percentage || 0}%`, 175, yPos);
        pdf.line(175, yPos + 1, 190, yPos + 1);
        yPos += 7;
        
        pdf.setFont(undefined, 'bold');
        pdf.text('E-MAIL ADDRESS', 20, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee2Email || '', 55, yPos);
        pdf.line(55, yPos + 1, 120, yPos + 1);
        
        pdf.setFont(undefined, 'bold');
        pdf.text('TELEPHONE', 130, yPos);
        pdf.setFont(undefined, 'normal');
        pdf.text(data.nominee2Phone || '', 160, yPos);
        pdf.line(160, yPos + 1, 190, yPos + 1);
        yPos += 15;
      }
      
      // Confirmation text
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');
      const confirmText = 'I confirm that I have read, understood and complied with all the membership terms and conditions as contained in the by-laws and the particulars I have given are true to the best of my belief.';
      const splitConfirm = pdf.splitTextToSize(confirmText, 170);
      pdf.text(splitConfirm, 20, yPos);
      yPos += splitConfirm.length * 5 + 10;
      
      pdf.setFontSize(10);
      pdf.text('Signature of Applicant: ____________________', 20, yPos);
      pdf.text('Date: _______________', 120, yPos);
      yPos += 20;
      
      // Official Use Only
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'bold');
      pdf.text('FOR OFFICIAL USE ONLY', 20, yPos);
      yPos += 10;
      
      pdf.setFontSize(10);
      pdf.text('CHECKED BY:', 20, yPos);
      yPos += 7;
      pdf.setFont(undefined, 'normal');
      pdf.text('Staff Name: ____________________', 20, yPos);
      yPos += 7;
      pdf.text('Designation: ____________________', 20, yPos);
      yPos += 7;
      pdf.text('Signature: ____________________', 20, yPos);
      pdf.text('Date: _______________', 120, yPos);
      yPos += 15;
      
      pdf.setFont(undefined, 'bold');
      pdf.text('AUTHORISED BY COMMITTEE', 20, yPos);
      yPos += 7;
      pdf.setFont(undefined, 'normal');
      pdf.text('Name: ____________________', 20, yPos);
      yPos += 7;
      pdf.text('Designation: ____________________', 20, yPos);
      yPos += 7;
      pdf.text('Signature: ____________________', 20, yPos);
      pdf.text('Date: _______________', 120, yPos);
      
      // Page footer
      pdf.setFontSize(9);
      pdf.text('Page | 3', 20, 285);
    }
    
    return pdf;
  };

  const onSubmit = async (data: MembershipFormData) => {
    // Critical validation: Passport photo MUST be uploaded
    if (!uploadedFiles.passportPhoto) {
      toast.error("Please upload your passport photograph before submitting");
      return;
    }
    
    if (!uploadedFiles.kraPinDocument || !uploadedFiles.idCopyDocument) {
      toast.error("Please upload all required documents (KRA PIN and ID Copy)");
      return;
    }

    setIsSubmitting(true);
    
    // Show loading toast
    const loadingToast = toast.loading("Generating your membership application PDF...");
    
    try {
      // Generate PDF with passport photo embedded
      const pdf = await generatePDF(data);
      const pdfBlob = pdf.output('blob');
      const filename = `NATCON_SACCO_Application_${data.fullNames.replace(/\s+/g, '_')}_${new Date().getTime()}.pdf`;
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      toast.success("PDF generated successfully with your passport photo!");

      // Prepare email content
      const subjectText = `Membership Application - ${data.fullNames} - ${data.idNumber}`;
      const bodyText = `Dear NATCON Staff SACCO Team,

I am writing to submit my membership application to join NATCON Staff Sacco.

Personal Details:
Name: ${data.fullNames}
ID Number: ${data.idNumber}
Department: ${data.department}
Staff Number: ${data.staffNumber}
Mobile Phone: ${data.mobilePhone}
Email: ${data.emailAddress}

Payment Commitment:
Monthly Contribution: Ksh. ${data.monthlyContribution.toLocaleString()}
Payment Method: ${data.paymentMethod}
Start Month: ${data.startMonth}

Nominee Information:
Primary Nominee: ${data.nominee1FullNames} (${data.nominee1Percentage}%)${data.nominee2FullNames ? `
Secondary Nominee: ${data.nominee2FullNames} (${data.nominee2Percentage || 0}%)` : ''}

Please find attached:
1. Completed membership application form (PDF)
2. KRA PIN document
3. ID/Passport copy
4. Passport photo

I have read and agreed to all terms and conditions of membership.

Thank you for considering my application.

Best regards,
${data.fullNames}
Staff Number: ${data.staffNumber}
Mobile: ${data.mobilePhone}`;

      // Create file objects for sharing
      const files: File[] = [
        new File([pdfBlob], filename, { type: 'application/pdf' })
      ];

      // Add uploaded documents to files array
      if (uploadedFiles.kraPinDocument) {
        files.push(new File([uploadedFiles.kraPinDocument], `KRA_PIN_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.kraPinDocument.name.split('.').pop()}`, { type: uploadedFiles.kraPinDocument.type }));
      }
      if (uploadedFiles.idCopyDocument) {
        files.push(new File([uploadedFiles.idCopyDocument], `ID_Copy_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.idCopyDocument.name.split('.').pop()}`, { type: uploadedFiles.idCopyDocument.type }));
      }
      if (uploadedFiles.passportPhoto) {
        files.push(new File([uploadedFiles.passportPhoto], `Passport_Photo_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.passportPhoto.name.split('.').pop()}`, { type: uploadedFiles.passportPhoto.type }));
      }

      // Check if Web Share API is available and can share files
      if ('share' in navigator && 'canShare' in navigator) {
        // Check if the browser can share files
        if (navigator.canShare({ files })) {
          try {
            await navigator.share({
              files,
              title: subjectText,
              text: `To: natconsacco@nca.go.ke\nSubject: ${subjectText}\n\n${bodyText}`,
            });
            
            toast.success("Email client opened with application and documents attached. Complete sending from your email app.");
            form.reset();
            setUploadedFiles({});
            return;
          } catch (error: any) {
            // User cancelled or share failed
            if (error.name !== 'AbortError') {
              console.warn('Share failed:', error);
            }
          }
        }
      }

      // Fallback: Download all files and open email with instructions
      // Download PDF
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const pdfLink = document.createElement('a');
      pdfLink.href = pdfUrl;
      pdfLink.download = filename;
      pdfLink.style.display = 'none';
      document.body.appendChild(pdfLink);
      pdfLink.click();
      document.body.removeChild(pdfLink);
      URL.revokeObjectURL(pdfUrl);

      // Download KRA PIN document
      if (uploadedFiles.kraPinDocument) {
        const kraUrl = URL.createObjectURL(uploadedFiles.kraPinDocument);
        const kraLink = document.createElement('a');
        kraLink.href = kraUrl;
        kraLink.download = `KRA_PIN_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.kraPinDocument.name.split('.').pop()}`;
        kraLink.style.display = 'none';
        document.body.appendChild(kraLink);
        kraLink.click();
        document.body.removeChild(kraLink);
        URL.revokeObjectURL(kraUrl);
      }

      // Download ID Copy document
      if (uploadedFiles.idCopyDocument) {
        const idUrl = URL.createObjectURL(uploadedFiles.idCopyDocument);
        const idLink = document.createElement('a');
        idLink.href = idUrl;
        idLink.download = `ID_Copy_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.idCopyDocument.name.split('.').pop()}`;
        idLink.style.display = 'none';
        document.body.appendChild(idLink);
        idLink.click();
        document.body.removeChild(idLink);
        URL.revokeObjectURL(idUrl);
      }

      // Download Passport Photo
      if (uploadedFiles.passportPhoto) {
        const photoUrl = URL.createObjectURL(uploadedFiles.passportPhoto);
        const photoLink = document.createElement('a');
        photoLink.href = photoUrl;
        photoLink.download = `Passport_Photo_${data.fullNames.replace(/\s+/g, '_')}.${uploadedFiles.passportPhoto.name.split('.').pop()}`;
        photoLink.style.display = 'none';
        document.body.appendChild(photoLink);
        photoLink.click();
        document.body.removeChild(photoLink);
        URL.revokeObjectURL(photoUrl);
      }

      // Create email with pre-filled content
      const subject = encodeURIComponent(subjectText);
      const body = encodeURIComponent(bodyText);
      const mailtoLink = `mailto:natconsacco@nca.go.ke?subject=${subject}&body=${body}`;
      
      // Wait a moment for downloads to start, then open email
      setTimeout(() => {
        window.location.href = mailtoLink;
        
        toast.success("Files downloaded and email client opening. Please attach the downloaded files and send.");
      }, 2000);

      form.reset();
      setUploadedFiles({});
    } catch (error: any) {
      toast.error("Failed to generate application. Please try again.");
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
      <Helmet>
        <title>NATCON Staff Sacco Membership Application Form</title>
        <meta name="description" content="A modern, highly effective web-based application form for new members of the NATCON Staff Sacco. The page is designed for a seamless user experience, incorporating elegant animations and a clean, responsive layout." />
        <meta name="keywords" content="NATCON, Staff Sacco, membership, application, form" />
        <link rel="canonical" href="/membership-application" />
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent mb-4">
              NATCON Staff Sacco
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground/90 mb-6">
              Membership Application Form
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Join the NATCON Staff Sacco by completing this form. All information is confidential and will be used solely for membership processing.
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              
              {/* Personal Information Section */}
              <Card className="form-section-card animate-fade-in hover-scale">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Personal Information
                  </CardTitle>
                  <CardDescription>
                    Please provide your personal details as they appear on your official documents.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="fullNames"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Names (Mr/Mrs/Ms)</FormLabel>
                          <FormControl>
                            <Input placeholder="As they appear on ID" {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="idNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ID Number</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="department"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Department</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="dutyStation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Duty Station</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="staffNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Staff Number</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                      control={form.control}
                      name="postalAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postal Address</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="postCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Post Code</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="town"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Town</FormLabel>
                          <FormControl>
                            <Input {...field} className="form-input" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="officeTelephone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Office Telephone Number</FormLabel>
                          <FormControl>
                            <Input type="tel" {...field} className="form-input" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="mobilePhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mobile Phone Number *</FormLabel>
                          <FormControl>
                            <Input type="tel" {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="emailAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>E-mail Address *</FormLabel>
                        <FormControl>
                          <Input type="email" {...field} className="form-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="border-t pt-6 mt-6">
                    <h4 className="font-semibold mb-4">Passport Photo Upload</h4>
                    <div className="space-y-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-4">
                          <Input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload('passportPhoto', file);
                            }}
                            className="form-input"
                            id="passport-photo-upload"
                          />
                          {uploadedFiles.passportPhoto && (
                            <span className="text-sm text-primary font-medium">
                              ✓ Uploaded
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Upload a recent passport-size photo (JPEG/PNG, max 10MB)
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <h4 className="font-semibold mb-4">Witness Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="witnessName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Witness (Sacco Member) Name *</FormLabel>
                            <FormControl>
                              <Input {...field} className="form-input" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="witnessIdNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Witness ID Number *</FormLabel>
                            <FormControl>
                              <Input {...field} className="form-input" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="witnessDutyStation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Witness Duty Station</FormLabel>
                            <FormControl>
                              <Input {...field} className="form-input" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Commitment Section */}
              <Card className="form-section-card animate-fade-in hover-scale">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-primary" />
                    Payment Commitment
                  </CardTitle>
                  <CardDescription>
                    Please specify your monthly contribution and preferred payment method.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="monthlyContribution"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>I hereby commit to pay NATCON Sacco Ltd Kshs. *</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              min="2000"
                              {...field}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                              className="form-input"
                            />
                          </FormControl>
                          <FormDescription>Minimum contribution: Ksh. 2,000</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="startMonth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>With effect from the month of *</FormLabel>
                          <FormControl>
                            <Input type="month" {...field} className="form-input" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="paymentMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Monthly through: *</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex flex-col space-y-2"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Payslip Checkoff" id="payslip" />
                              <label htmlFor="payslip">Payslip Checkoff</label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Direct Deposit" id="direct" />
                              <label htmlFor="direct">Direct Deposit</label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="registeredMpesa"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Registered M-Pesa Number *</FormLabel>
                        <FormControl>
                          <Input type="tel" {...field} className="form-input" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="bg-accent/20 p-4 rounded-lg border border-accent">
                    <h4 className="font-semibold mb-2">For Direct Deposits:</h4>
                    <p><strong>Paybill Number:</strong> 400222</p>
                    <p><strong>Account Number:</strong> 412443#[name][purpose]</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Example: 412443#janedoeLoan
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Nominated Beneficiaries Section */}
              <MembershipNomineesSection form={form} />

              {/* Required Attachments Section */}
              <MembershipDocumentsSection 
                uploadedFiles={uploadedFiles}
                onFileUpload={handleFileUpload}
              />

              {/* Terms and Conditions Section */}
              <MembershipTermsSection 
                form={form}
                isSubmitting={isSubmitting}
              />
              
            </form>
          </Form>
        </div>
      </main>

      <Footer />
    </div>
  );
};